import { useEffect, useRef } from 'react'

import { UnmountApp } from 'CommonAppsEntrypoint'
import { unmountApp } from 'modules/app-registry/hooks/useUnmountApp'
import { useUserContext } from 'shared/hooks'
import { logger } from 'shared/services/logger'

import {
  FederatedComponentMeta,
  useFederatedComponent
} from './useFederatedComponent'

export const useMountApp = (
  { url, scope, entryPath, envKey, cdnUrl }: FederatedComponentMeta,
  appProps: object,
  isAuthRequired: boolean = true
) => {
  const { user } = useUserContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const unmountRef = useRef<UnmountApp>(undefined)
  const isLoadingDisabled = isAuthRequired && !user

  const { module, isError, isLoaded, missingRemoteUrl } = useFederatedComponent(
    url,
    scope,
    entryPath,
    envKey,
    isLoadingDisabled,
    cdnUrl
  )
  useEffect(() => {
    try {
      const containerNode = containerRef.current
      if (!containerNode || isLoadingDisabled || !module) {
        return
      }

      unmountRef.current = module.mountApp(containerNode, appProps)
    } catch (error) {
      unmountRef.current = undefined
      logger.exception(
        'federation.mount_failed',
        error,
        { scope, entry: entryPath },
        { category: 'federation' }
      )
    }
  }, [appProps, entryPath, isLoadingDisabled, module, scope])

  useEffect(() => {
    const container = containerRef.current
    return () => {
      if (unmountRef.current) {
        unmountRef.current()
      } else {
        unmountApp(container)
      }
    }
  }, [])

  return { ref: containerRef, isLoaded, isError, missingRemoteUrl }
}
